<div class="projects-testlist-container">
  <div class="batch-selection-section mb-4">
    <div class="row">
      <div class="col-12">
        <div class="alert alert-info" role="alert">
          <i class="fa fa-info-circle me-2"></i>
          <strong>Step 1:</strong> Please select a batch to proceed with the testlist.
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12 col-md-6">
        <div class="filter-group">
          <select-field label="Select Batch" [selectFormControl]="getSelectedBatchControl()"
            [options]="batches" (selectionChange)="onBatchChange($event)">
          </select-field>
        </div>
        @if (teamConflictError) {
          <div class="alert alert-danger mt-3">
            <i class="fa fa-times-circle me-2"></i>
            <strong>{{ teamConflictErrorMessage }}</strong>
          </div>
        }
      </div>
    </div>
  </div>

  @if (isBatchSelected() && !teamConflictError) {
    <div>
      <div class="row">
        <div class="col-sm-12 col-md-8">
          <projects-round-testlist [projectId]="projectId" [batchId]="selectedBatchId ?? undefined"
            [initialRoundData]="getCurrentBatchRoundData()" [readonly]="false"
            [restrictToClientDeclaration]="restrictRoundToClientDeclaration"
            (onRoundStateChanged)="onRoundStateChanged($event)" (onRoundCompleted)="onRoundCompleted($event)"
            (onRoundDataUpdated)="onRoundDataUpdated($event)" />
        </div>
        <div class="col-sm-12 col-md-4">
          <div class="testlist-header">
            <div class="initial-description">
              <p><strong>Instructions:</strong> Review each test case carefully, upload evidence where
              required,
              and select the appropriate status (Pass, Light Pass, Fail, Light Fail, N/A, or Pending) for each test.
              Use the global Evidence section to upload files that apply to multiple tests,
            and add comments to provide additional context or notes about the testing process.</p>
          </div>
          <div class="legend">
            <div class="legend-item">
              <span class="legend-color test-failed"></span>
              <span>Test Failed</span>
            </div>
            <div class="legend-item">
              <span class="legend-color test-failed-light"></span>
              <span>Test Light Failed</span>
            </div>
            <div class="legend-item">
              <span class="legend-color test-pending"></span>
              <span>Test Pending</span>
            </div>
            <div class="legend-item">
              <span class="legend-color test-passed"></span>
              <span>Test Passed</span>
            </div>
            <div class="legend-item">
              <span class="legend-color test-passed-light"></span>
              <span>Test Light Passed</span>
            </div>
            <div class="legend-item">
              <span class="legend-color test-na"></span>
              <span>Test N/A</span>
            </div>
          </div>
          <div class="client-info-message">
            <p class="client-info-title">INFORMATION TO THE CLIENT</p>
            <p>The Issue List reflects only the issues identified from test lists already reviewed by Global Lab testers.</p>
            <p>Test lists in Pending (yellow) status have not yet been reviewed. Additional issues may be identified once these tests are performed.</p>
            <p>Therefore, the absence of open issues at a given moment does not indicate completion of the certification process.</p>
          </div>
        </div>
      </div>
    </div>
    <div class="filters-section">
      <div class="row">
        <p class="description">
          Select a test from the left column; the right column will show related assignee.
        </p>
      </div>
      <div class="filter-row">
        <div class="filter-group">
          <label>Filter by checklist</label>
          <select-field [selectFormControl]="getSelectedChecklistControl()" [options]="checklists"
            (selectionChange)="onChecklistChange($event)">
          </select-field>
        </div>
        <div class="filter-group">
          <label>Filter by Tester</label>
          <select-field [selectFormControl]="getSelectedTesterControl()" [options]="testers"
            (selectionChange)="onTesterChange($event)">
          </select-field>
        </div>
      </div>
    </div>
    <div class="main-content" #mainContentRef>
      <div class="tests-column">
        <div class="column-header">
          <h4>TESTS</h4>
          <span>REVIEWED</span>
        </div>
        <div class="tests-list">
          @for (macroTest of macroTests; track macroTest; let i = $index) {
            <div class="test-item"
              [class]="getMacroTestDynamicClass(macroTest)"
              [class.selected]="selectedMacroTest?.id === macroTest.id" (click)="selectMacroTest(macroTest)">
              <div class="test-name">{{ macroTest.testlist.name }}</div>
              @if (macroTest.reviewConfirmed) {
                <div class="test-review-status">
                  <i class="fa fa-check review-check"></i>
                </div>
              }
            </div>
          }
        </div>
      </div>
      @if (selectedMacroTest) {
        <div class="details-column position-relative">
          @if (selectedMacroTest.stepsLoading) {
            <loader [config]="stepsLoaderConfig" />
          }
          <div class="test-details-header">
            <h4>{{ selectedMacroTest.name }}</h4>
            <span class="status-badge" [ngClass]="'badge-' + (selectedMacroTest.status ?? 'pending')">
                        <i class="fa me-1" [ngClass]="{
                            'fa-check': selectedMacroTest.status === 'passed',
                            'fa-times': selectedMacroTest.status === 'failed',
                            'fa-reply': selectedMacroTest.status === 'na',
                            'fa-clock-o': !selectedMacroTest.status || selectedMacroTest.status === 'pending'
                        }"></i>
              {{ getTestStatusLabel(selectedMacroTest.status ?? null) }}
            </span>
          </div>
          <div class="test-content">
            <div class="test-info">
              <div class="test-description">
                <div class="rules-section">
                  <div class="rules-section-header" (click)="toggleRulesSection()">
                    <span class="rules-section-title">Rules to Observe</span>
                    @if (!selectedMacroTest.rulesLoading && selectedMacroTest.rules?.length) {
                      <span class="rules-count-badge">{{ selectedMacroTest.rules?.length }}</span>
                    }
                    <i class="fa ms-auto rules-collapse-icon"
                       [class.fa-chevron-down]="rulesCollapsed"
                       [class.fa-chevron-up]="!rulesCollapsed">
                    </i>
                  </div>
                  @if (!rulesCollapsed) {
                    <div class="rules-section-body">
                      @if (selectedMacroTest.rulesLoading) {
                        <p>Loading rules...</p>
                      }
                      @if (!selectedMacroTest.rulesLoading && (!selectedMacroTest.rules || selectedMacroTest.rules.length === 0)) {
                        <p>{{ selectedMacroTest.description || 'No specific procedure defined' }}</p>
                      }
                      @if (!selectedMacroTest.rulesLoading && selectedMacroTest.rules && selectedMacroTest.rules.length > 0) {
                        <div class="rules-accordion">
                          @for (rule of selectedMacroTest.rules; track rule) {
                            <div class="rule-accordion-item">
                              <div class="rule-header" (click)="toggleRule(rule)">
                                <span class="rule-title">{{ rule.title }}</span>
                                <i class="expand-icon" [class.expanded]="rule.expanded">
                                  {{ rule.expanded ? '▲' : '▼' }}
                                </i>
                              </div>
                              @if (rule.expanded) {
                                <div class="rule-content">
                                  <p>{{ rule.content }}</p>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
            <div class="review-section" [hidden]="!canViewReviewCheckbox">
              <h6>REVIEW</h6>
              <div class="review-content">
                <div class="review-confirmation">
                  <div class="checkbox-wrapper">
                    <label for="reviewCheckbox" class="checkbox-label">
                      Confirmation of the testlist review:
                    </label>
                    <input type="checkbox" id="reviewCheckbox"
                      [checked]="selectedMacroTest.reviewConfirmed || false"
                      [disabled]="!canToggleReviewConfirmation()"
                      [title]="canEditReviewCheckbox && !canToggleReviewConfirmation()
                        ? 'All tests (with and without evidence) must be Passed before you can confirm the review'
                        : null"
                      (change)="confirmReview()">
                  </div>
                </div>
              </div>
            </div>
            <div class="test-sections row">
              <div class="test-section col-sm-12 col-md-6">
                <h6>VERIFICATION WITHOUT EVIDENCE</h6>
                <div class="verification-steps">
                  @if (getWithoutEvidenceTests().length === 0) {
                    <div class="alert alert-info">
                      No verification steps without evidence for this testlist.
                    </div>
                  }
                  @for (test of getWithoutEvidenceTests(); track test) {
                    <div class="verification-step">
                      <div class="step-content">
                        <p><strong>{{ test.title }}</strong></p>
                        <div class="step-actions">
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'passed' ? 'btn btn-success' : 'btn btn-outline-success'"
                            (click)="setIndividualTestStatus(test.id, 'passed')"
                            [disabled]="!canEditTestlist || isTestStatusModified(test.id)">
                            <i class="fa fa-check me-1"></i>
                            Pass
                          </button>
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'failed' ? 'btn btn-danger' : 'btn btn-outline-danger'"
                            (click)="setIndividualTestStatus(test.id, 'failed')"
                            [disabled]="!canEditTestlist || isTestStatusModified(test.id)">
                            <i class="fa fa-times me-1"></i>
                            Fail
                          </button>
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'na' ? 'btn btn-secondary' : 'btn btn-outline-secondary'"
                            (click)="setIndividualTestStatus(test.id, 'na')"
                            [disabled]="!canEditTestlist || isTestStatusModified(test.id)">
                            <i class="fa fa-reply me-1"></i>
                            N/A
                          </button>
                          @if (isTestStatusModified(test.id)) {
                            <button type="button" class="btn btn-primary ms-2"
                              [disabled]="!canEditTestlist"
                              (click)="saveTestStatus(test.id)">
                              <i class="fa fa-save me-1"></i>
                              Save
                            </button>
                          }
                          @if (isTestStatusModified(test.id)) {
                            <button type="button" class="btn btn-outline-secondary ms-1"
                              [disabled]="!canEditTestlist"
                              (click)="cancelTestStatusChange(test.id)">
                              <i class="fa fa-times me-1"></i>
                              Cancel
                            </button>
                          }
                          <i class="fa fa-info-circle test-log-icon ms-2"
                            [class.flash-log-icon]="shouldFlashTestLogIcon(test)"
                            title="View test log"
                          (click)="openTestLogModal(test, $event)"></i>
                        </div>
                        @if (test.lastUpdate) {
                          <div class="step-last-update">Last update: {{ test.lastUpdate }}</div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div class="test-section col-sm-12 col-md-6"
                [class.with-evidence-locked]="!areWithoutEvidenceTestsCompleted()">
                <h6>VERIFICATION WITH EVIDENCE</h6>
                @if (!areWithoutEvidenceTestsCompleted()) {
                  <div class="alert alert-warning with-evidence-lock-message">
                    <i class="fa fa-lock me-2"></i>
                    Complete all <strong>Verification Without Evidence</strong> steps first.
                  </div>
                }
                <div class="verification-steps">
                  @if (getWithEvidenceTests().length === 0) {
                    <div class="alert alert-info">
                      No verification steps with evidence for this testlist.
                    </div>
                  }
                  @for (test of getWithEvidenceTests(); track test) {
                    <div class="verification-step">
                      <div class="step-content">
                        <p><strong>{{ test.title }}</strong></p>
                        <div class="step-actions">
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'passed' ? 'btn btn-success' : 'btn btn-outline-success'"
                            (click)="setIndividualTestStatus(test.id, 'passed')"
                            [disabled]="!canEditTestlist || !areWithoutEvidenceTestsCompleted() || isTestStatusModified(test.id)">
                            <i class="fa fa-check me-1"></i>
                            Pass
                          </button>
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'failed' ? 'btn btn-danger' : 'btn btn-outline-danger'"
                            (click)="setIndividualTestStatus(test.id, 'failed')"
                            [disabled]="!canEditTestlist || !areWithoutEvidenceTestsCompleted() || isTestStatusModified(test.id)">
                            <i class="fa fa-times me-1"></i>
                            Fail
                          </button>
                          <button type="button"
                            [class]="getTestStatusForDisplay(test) === 'na' ? 'btn btn-secondary' : 'btn btn-outline-secondary'"
                            (click)="setIndividualTestStatus(test.id, 'na')"
                            [disabled]="!canEditTestlist || !areWithoutEvidenceTestsCompleted() || isTestStatusModified(test.id)">
                            <i class="fa fa-reply me-1"></i>
                            N/A
                          </button>
                          @if (isTestStatusModified(test.id)) {
                            <button type="button" class="btn btn-primary ms-2"
                              (click)="saveTestStatus(test.id)"
                              [disabled]="!canEditTestlist || !areWithoutEvidenceTestsCompleted()">
                              <i class="fa fa-save me-1"></i>
                              Save
                            </button>
                          }
                          @if (isTestStatusModified(test.id)) {
                            <button type="button" class="btn btn-outline-secondary ms-1"
                              (click)="cancelTestStatusChange(test.id)"
                              [disabled]="!canEditTestlist || !areWithoutEvidenceTestsCompleted()">
                              <i class="fa fa-times me-1"></i>
                              Cancel
                            </button>
                          }
                          <i class="fa fa-info-circle test-log-icon ms-2"
                            [class.flash-log-icon]="shouldFlashTestLogIcon(test)"
                            title="View test log"
                          (click)="openTestLogModal(test, $event)"></i>
                        </div>
                        @if (test.lastUpdate) {
                          <div class="step-last-update">Last update: {{ test.lastUpdate }}</div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      @if (!selectedMacroTest) {
        <div class="details-column empty-state">
          <div class="empty-content">
            <h4>Choose a testlist to run</h4>
            <p>Select a test from the left column to view details and run the test.</p>
          </div>
        </div>
      }
    </div>
    @if (selectedMacroTest) {
      <div class="evidence-section" style="position: relative;">
        <div class="evidence-header">
          <h4>Evidence</h4>
        </div>
        <div class="evidence-content mt-2">
          <div class="file-upload-area row">
            <div class="col-sm-12 col-md-7">
              <input type="file" #globalFileInput id="global-evidence-file" multiple
                (change)="onGlobalFileSelected($event)" style="display: none;">
              <div class="file-upload-wrapper" [hidden]="!canUploadEvidence">
                <button type="button" class="btn btn-outline-primary w-100"
                  (click)="triggerGlobalFileInput()" [disabled]="!canEditTestlist || !selectedMacroTest">
                  <i class="fa fa-file me-2"></i>
                  Choose File
                </button>
                <div class="file-status-text mt-3">
                  @if (globalEvidenceFiles.length === 0) {
                    <span>
                      No file selected
                    </span>
                  } @else {
                    {{ globalEvidenceFiles.length }} file(s) selected
                  }
                </div>
              </div>
              @if (globalEvidenceFiles.length > 0) {
                <div class="global-uploaded-files">
                  @for (file of globalEvidenceFiles; track file; let fileIndex = $index) {
                    <div
                      class="global-file-item">
                      <span class="file-name">{{ file.fileName }}</span>
                      @if (file.url) {
                        <button type="button" class="btn btn-sm btn-outline-secondary"
                          (click)="downloadGlobalEvidenceFile(file)">
                          <i class="fa fa-solid fa-download"></i>
                        </button>
                      }
                      @if (canUploadEvidence) {
                        <button type="button" class="btn btn-sm btn-outline-danger"
                          (click)="removeGlobalFile(fileIndex)">
                          <i class="fa fa-times"></i>
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            </div>
            <div class="global-actions col-sm-12 col-md-5">
              <button type="button"
                [class]="selectedMacroTest.status === 'passed' ? 'btn btn-success' : 'btn btn-outline-success'"
                [disabled]="!canEditTestlist || isMacroTestButtonDisabled('passed')"
                (click)="passTest()">
                <i class="fa fa-check me-1"></i>
                Pass
              </button>
              <button type="button"
                [class]="selectedMacroTest.status === 'pending' ? 'btn btn-warning' : 'btn btn-outline-warning'"
                [disabled]="!canEditTestlist || isMacroTestButtonDisabled('pending')"
                (click)="pendingTest()">
                <i class="fa fa-reply me-1"></i>
                Pending
              </button>
              <button type="button"
                [class]="selectedMacroTest.status === 'failed' ? 'btn btn-danger' : 'btn btn-outline-danger'"
                [disabled]="!canEditTestlist || isMacroTestButtonDisabled('failed')"
                (click)="failTest()">
                <i class="fa fa-times me-1"></i>
                Fail
              </button>
              <button type="button"
                [class]="selectedMacroTest.status === 'na' ? 'btn btn-secondary' : 'btn btn-outline-secondary'"
                [disabled]="!canEditTestlist || isMacroTestButtonDisabled('na')"
                (click)="naTest()">
                <i class="fa fa-reply me-1"></i>
                N/A
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="notes-section">
        <notes-customers-form
          [customer]="testlistNotes"
          [readonlyCustomerNotes]="!canEditTestlist"
          [customerId]="projectId"
          [noteServiceMethod]="projectsService"
          [secondaryId]="selectedMacroTest.id"
          [listNoteMethod]="'listTestlistNotes'"
          [addNoteMethod]="'createTestlistNotes'"
          [deleteNoteMethod]="'deleteTestlistNotes'">
        </notes-customers-form>

        <projects-history-logs class="d-block mt-3" [projectId]="projectId"></projects-history-logs>
      </div>
    }
  </div>
} @else {
  @if (teamConflictError) {
    <div>
      <div class="row">
        <div class="col-12">
          <projects-round-testlist [projectId]="projectId" [batchId]="selectedBatchId ?? undefined"
            [initialRoundData]="getCurrentBatchRoundData()" [readonly]="false"
            [restrictToClientDeclaration]="restrictRoundToClientDeclaration"
            (onRoundStateChanged)="onRoundStateChanged($event)"
            (onRoundCompleted)="onRoundCompleted($event)"
            (onRoundDataUpdated)="onRoundDataUpdated($event)" />
        </div>
      </div>
    </div>
  } @else {
    <div class="no-batch-selected">
      <div class="alert alert-warning text-center" role="alert">
        <i class="fa fa-exclamation-triangle fa-3x mb-3"></i>
        <h4>No Batch Selected</h4>
        <p>Please select a batch from the dropdown above to continue working with the testlist.</p>
        <p class="text-muted mb-0">Once you select a batch, you will be able to view and work with the
        associated checklists and tests.</p>
      </div>
    </div>
  }
}


</div>

